import type { Viewport } from './types';
import { pan, zoom } from './viewport';

export type PanZoomConfig = {
  minScale?: number;
  maxScale?: number;
  /** Sensibilité du zoom à la molette. Plus grand = zoom plus rapide. */
  zoomSensitivity?: number;
  /**
   * Si fourni et retourne `false`, le drag souris ne pan pas (utile quand un autre
   * outil — pinceau — utilise le drag). La molette zoome toujours.
   */
  shouldPanOnMouseDrag?: () => boolean;
  /**
   * Idem pour le drag tactile à 1 doigt. Le pinch 2 doigts zoome toujours.
   */
  shouldPanOnTouchDrag?: () => boolean;
};

/**
 * Branche un canvas à un viewport mutable pour gérer pan + zoom souris/molette/touch.
 *
 * @param canvas l'élément HTMLCanvasElement à écouter
 * @param getViewport accesseur du viewport courant (recalcule à chaque event)
 * @param setViewport callback appelé quand le viewport change (le composant Svelte
 *   met à jour son état et appelle `renderer.draw()`)
 * @param config bornes et sensibilité optionnelles
 *
 * @returns une fonction `detach()` à appeler en `onDestroy` pour retirer
 *   les listeners (essentiel sinon : fuites mémoire).
 *
 * Comportement :
 * - **Mouse drag** : mousedown sur le canvas + mousemove (sur la window pour ne
 *   pas perdre le drag si le curseur sort du canvas) + mouseup.
 * - **Wheel** : zoom autour de la position de la souris. `preventDefault` pour
 *   éviter le scroll de la page.
 * - **Touch** : 1 doigt = pan, 2 doigts = pinch zoom autour du midpoint. La
 *   transition 1↔2 doigts est gérée proprement.
 */
export function attachPanZoom(
  canvas: HTMLCanvasElement,
  getViewport: () => Viewport,
  setViewport: (v: Viewport) => void,
  config: PanZoomConfig = {},
): () => void {
  let isDraggingMouse = false;
  let lastMouseX = 0;
  let lastMouseY = 0;

  type TouchState =
    | { kind: 'idle' }
    | { kind: 'pan'; x: number; y: number }
    | { kind: 'pinch'; dist: number };
  let touchState: TouchState = { kind: 'idle' };

  function canvasOffset(): { left: number; top: number } {
    const r = canvas.getBoundingClientRect();
    return { left: r.left, top: r.top };
  }

  // --- Mouse pan ---
  function onMouseDown(e: MouseEvent): void {
    if (config.shouldPanOnMouseDrag && !config.shouldPanOnMouseDrag()) return;
    isDraggingMouse = true;
    lastMouseX = e.clientX;
    lastMouseY = e.clientY;
  }
  function onMouseMove(e: MouseEvent): void {
    if (!isDraggingMouse) return;
    if (config.shouldPanOnMouseDrag && !config.shouldPanOnMouseDrag()) {
      isDraggingMouse = false;
      return;
    }
    const v = getViewport();
    // Δscreen → Δworld : on divise par scale pour que le drag se fasse à la même
    // vitesse perçue quel que soit le zoom.
    const dx = (e.clientX - lastMouseX) / v.scale;
    const dy = (e.clientY - lastMouseY) / v.scale;
    setViewport(pan(v, dx, dy));
    lastMouseX = e.clientX;
    lastMouseY = e.clientY;
  }
  function onMouseUp(): void {
    isDraggingMouse = false;
  }

  // --- Wheel zoom ---
  function onWheel(e: WheelEvent): void {
    e.preventDefault();
    const sensitivity = config.zoomSensitivity ?? 0.002;
    // deltaY > 0 = scroll vers le bas = zoom out (convention standard)
    const factor = Math.exp(-e.deltaY * sensitivity);
    const { left, top } = canvasOffset();
    const anchorX = e.clientX - left;
    const anchorY = e.clientY - top;
    const zoomOpts: { minScale?: number; maxScale?: number } = {};
    if (config.minScale !== undefined) zoomOpts.minScale = config.minScale;
    if (config.maxScale !== undefined) zoomOpts.maxScale = config.maxScale;
    setViewport(zoom(getViewport(), factor, anchorX, anchorY, zoomOpts));
  }

  // --- Touch ---
  function touchDistance(t1: Touch, t2: Touch): number {
    return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  }
  function onTouchStart(e: TouchEvent): void {
    if (e.touches.length >= 1) e.preventDefault();
    if (e.touches.length === 1) {
      // Le pinch 2 doigts zoom toujours, mais le pan 1 doigt peut être désactivé.
      if (config.shouldPanOnTouchDrag && !config.shouldPanOnTouchDrag()) {
        touchState = { kind: 'idle' };
        return;
      }
      const t = e.touches[0];
      if (!t) return;
      touchState = { kind: 'pan', x: t.clientX, y: t.clientY };
    } else if (e.touches.length === 2) {
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      if (!t1 || !t2) return;
      touchState = { kind: 'pinch', dist: touchDistance(t1, t2) };
    }
  }
  function onTouchMove(e: TouchEvent): void {
    e.preventDefault();
    const v = getViewport();
    if (touchState.kind === 'pan' && e.touches.length === 1) {
      const t = e.touches[0];
      if (!t) return;
      const dx = (t.clientX - touchState.x) / v.scale;
      const dy = (t.clientY - touchState.y) / v.scale;
      setViewport(pan(v, dx, dy));
      touchState = { kind: 'pan', x: t.clientX, y: t.clientY };
    } else if (touchState.kind === 'pinch' && e.touches.length === 2) {
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      if (!t1 || !t2) return;
      const newDist = touchDistance(t1, t2);
      const factor = newDist / touchState.dist;
      const { left, top } = canvasOffset();
      const anchorX = (t1.clientX + t2.clientX) / 2 - left;
      const anchorY = (t1.clientY + t2.clientY) / 2 - top;
      const zoomOpts: { minScale?: number; maxScale?: number } = {};
      if (config.minScale !== undefined) zoomOpts.minScale = config.minScale;
      if (config.maxScale !== undefined) zoomOpts.maxScale = config.maxScale;
      setViewport(zoom(v, factor, anchorX, anchorY, zoomOpts));
      touchState = { kind: 'pinch', dist: newDist };
    }
  }
  function onTouchEnd(e: TouchEvent): void {
    if (e.touches.length === 0) {
      touchState = { kind: 'idle' };
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      if (!t) return;
      touchState = { kind: 'pan', x: t.clientX, y: t.clientY };
    }
  }

  // --- Attach ---
  canvas.addEventListener('mousedown', onMouseDown);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('touchstart', onTouchStart, { passive: false });
  canvas.addEventListener('touchmove', onTouchMove, { passive: false });
  canvas.addEventListener('touchend', onTouchEnd);
  canvas.addEventListener('touchcancel', onTouchEnd);

  return function detach() {
    canvas.removeEventListener('mousedown', onMouseDown);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
    canvas.removeEventListener('wheel', onWheel);
    canvas.removeEventListener('touchstart', onTouchStart);
    canvas.removeEventListener('touchmove', onTouchMove);
    canvas.removeEventListener('touchend', onTouchEnd);
    canvas.removeEventListener('touchcancel', onTouchEnd);
  };
}
